'use client'

import type { TabData } from 'fukku/Tabs/types/Tabs'
import {
	type KeyboardEventHandler,
	type MouseEventHandler,
	forwardRef,
} from 'react'
import { cx } from 'utils/cx'

import styles from './Tab.module.scss'

export interface TabProps {
	readonly data: TabData
	readonly iconOnly?: boolean
	readonly isSelected: boolean
	readonly onClick?: MouseEventHandler
	readonly onKeyDown?: KeyboardEventHandler
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
	{ data, iconOnly, isSelected, onClick = () => {}, onKeyDown = () => {} },
	ref
): React.ReactNode {
	const { dataTestId, id, icon, selectedIcon, label } = data
	const tabIcon = isSelected ? selectedIcon ?? icon : icon

	return (
		<button
			ref={ref}
			key={id}
			type='button'
			id={id}
			tabIndex={isSelected ? 0 : -1}
			role='tab'
			aria-controls={`${id}.panel`}
			aria-selected={isSelected}
			data-testid={dataTestId ?? id}
			onClick={onClick}
			onKeyDown={onKeyDown}
			className={cx(styles.tab, isSelected && styles.selected)}
		>
			{!!tabIcon && tabIcon}
			<p
				className={cx(
					styles.label,
					tabIcon && styles.hasIcon,
					iconOnly && styles.iconOnly
				)}
			>
				{label}
			</p>
		</button>
	)
})
